/**
 * The composer's memory: a draft per conversation (kept for a week in this browser) and the
 * messages waiting to send while a reply runs, first in, first out. Pure apart from storage.
 */
import { create } from 'zustand';

const DRAFT_PREFIX = 'orgus.assistant.draft:';
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;
/** The key for a conversation's draft; a new conversation has its own. */
export const draftKey = (conversationId: string | null) => `${DRAFT_PREFIX}${conversationId ?? 'new'}`;

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function readDraft(conversationId: string | null, now = Date.now()): string {
  const raw = storage()?.getItem(draftKey(conversationId));
  if (!raw) return '';
  try {
    const d = JSON.parse(raw) as { text?: unknown; at?: unknown };
    if (typeof d.text !== 'string' || typeof d.at !== 'number' || now - d.at > DRAFT_TTL_MS) return '';
    return d.text;
  } catch {
    return '';
  }
}

export function writeDraft(conversationId: string | null, text: string, now = Date.now()): void {
  const s = storage();
  if (!s) return;
  try {
    if (text.trim()) s.setItem(draftKey(conversationId), JSON.stringify({ text, at: now }));
    else s.removeItem(draftKey(conversationId));
  } catch {
    // Storage full or blocked: the draft lives only in the page.
  }
}

/** Conversations with a draft, for the list's "Draft" marker. */
export function draftIds(now = Date.now()): Set<string> {
  const s = storage();
  const out = new Set<string>();
  if (!s) return out;
  for (let i = 0; i < s.length; i++) {
    const k = s.key(i);
    if (!k?.startsWith(DRAFT_PREFIX)) continue;
    const id = k.slice(DRAFT_PREFIX.length);
    if (readDraft(id === 'new' ? null : id, now)) out.add(id);
  }
  return out;
}

export interface QueuedMessage {
  id: string;
  text: string;
}

interface QueueStore {
  byConversation: Record<string, QueuedMessage[]>;
  add: (conversationId: string, text: string) => void;
  edit: (conversationId: string, id: string, text: string) => void;
  remove: (conversationId: string, id: string) => void;
  /** Take the oldest message off the queue. */
  shift: (conversationId: string) => QueuedMessage | null;
}

let seq = 0;

/** Messages written while a reply runs, sent one by one as each reply finishes. */
export const useQueue = create<QueueStore>()((set, get) => ({
  byConversation: {},
  add: (c, text) => set((st) => ({ byConversation: { ...st.byConversation, [c]: [...(st.byConversation[c] ?? []), { id: `q${++seq}`, text }] } })),
  edit: (c, id, text) =>
    set((st) => ({
      byConversation: {
        ...st.byConversation,
        [c]: text.trim() ? (st.byConversation[c] ?? []).map((m) => (m.id === id ? { ...m, text } : m)) : (st.byConversation[c] ?? []).filter((m) => m.id !== id),
      },
    })),
  remove: (c, id) => set((st) => ({ byConversation: { ...st.byConversation, [c]: (st.byConversation[c] ?? []).filter((m) => m.id !== id) } })),
  shift: (c) => {
    const list = get().byConversation[c] ?? [];
    if (!list.length) return null;
    set((st) => ({ byConversation: { ...st.byConversation, [c]: list.slice(1) } }));
    return list[0]!;
  },
}));

const EMPTY: QueuedMessage[] = [];
export const useQueued = (conversationId: string | null): QueuedMessage[] => useQueue((s) => (conversationId ? (s.byConversation[conversationId] ?? EMPTY) : EMPTY));
